export const formatDate = (date: string) => {
    // If the date is in the format YYYY-MM-DD, add time to ensure correct date is displayed, instead of falling back 1 minute to previous day due to missing timezone data.
    const dateObj = date.includes('T') ? new Date(date) : new Date(date + 'T00:00:00')
    return dateObj.toLocaleString(undefined, {
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    })
}

/**
 * Whole years between a date of birth and today, or null when it cannot be told.
 *
 * APP-1717. Shown beside the date of birth so the outreach team can see at a
 * glance whether to ring the patient or a parent/guardian. Not specific to any
 * study — every study that collects a date of birth can use it.
 *
 * ## Why this parses the string rather than `new Date(dateOfBirth)`
 *
 * `new Date('2005-09-28')` is UTC midnight. In any negative-offset timezone that
 * is the 27th locally, so `getMonth()`/`getDate()` report the day before and the
 * birthday lands a day early — which puts the age a year out on the boundary,
 * exactly where it matters for deciding who to call. `formatDate` above works
 * around the same hazard by appending `T00:00:00`; reading the components
 * straight out of the string avoids it rather than compensating for it, and
 * handles a full ISO timestamp equally well.
 *
 * Returns null rather than a number for a missing, unparseable or future date,
 * so a typo shows nothing instead of a confident wrong answer.
 */
export const calculateAge = (dateOfBirth: string | null | undefined): number | null => {
    if (!dateOfBirth) return null

    const [year, month, day] = dateOfBirth.slice(0, 10).split('-').map(Number)
    if (!year || !month || !day || month < 1 || month > 12 || day < 1 || day > 31) return null

    const today = new Date()
    let age = today.getFullYear() - year

    // Their birthday has not come round yet this year.
    const monthsToGo = month - (today.getMonth() + 1)
    if (monthsToGo > 0 || (monthsToGo === 0 && day > today.getDate())) age -= 1

    return age >= 0 && age < 150 ? age : null
}
